<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDismissablePopover } from '@/composables/useDismissablePopover'
import { listRaces } from '@/api/events'
import type { Race } from '@/types/events'

const props = defineProps<{
  currentEventSlug: string
  currentEditionSlug: string
  currentRaceSlug: string
}>()

const emit = defineEmits<{ (e: 'navigate'): void }>()

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const rootRef = ref<HTMLElement | null>(null)

// Closes on an outside click only (no Escape or focus handling), as the sidebar switchers always did.
const switcher = useDismissablePopover({
  idPrefix: 'race-switcher',
  boundaryRef: rootRef,
  singleOpen: true,
  closeOnOutside: ['click'],
})
const open = switcher.isOpen
const races = ref<Race[]>([])
const loading = ref(false)

const currentRace = computed(() =>
  races.value.find((r) => r.slug === props.currentRaceSlug) ?? null,
)

watch(() => props.currentEditionSlug, () => {
  races.value = []
  void loadRaces()
})

async function loadRaces(): Promise<void> {
  if (races.value.length > 0) return
  loading.value = true
  try {
    races.value = await listRaces(props.currentEventSlug, props.currentEditionSlug)
  } finally {
    loading.value = false
  }
}

function toggleOpen(): void {
  switcher.toggle()
  if (open.value) void loadRaces()
}

async function selectRace(race: Race): Promise<void> {
  switcher.close()
  emit('navigate')
  const raceBase = `/organizer/events/${props.currentEventSlug}/editions/${props.currentEditionSlug}/races/${props.currentRaceSlug}`
  const newBase = `/organizer/events/${props.currentEventSlug}/editions/${props.currentEditionSlug}/races/${race.slug}`
  const subPath = route.path.startsWith(raceBase) ? route.path.slice(raceBase.length) : ''
  await router.push(`${newBase}${subPath}`)
}

onMounted(() => {
  void loadRaces()
})

</script>

<template>
  <div ref="rootRef" data-race-switcher class="relative px-3 py-3 border-b border-slate-800">
    <button
      type="button"
      class="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors text-left"
      @click="toggleOpen"
    >
      <div class="flex-1 min-w-0">
        <p class="text-xs text-slate-500 font-medium uppercase tracking-wider">
          {{ t('nav.sidebar.race') }}
        </p>
        <p class="text-sm text-white font-semibold truncate mt-0.5">
          {{ currentRace?.name ?? '...' }}
        </p>
      </div>
      <svg
        class="w-4 h-4 text-slate-400 shrink-0 transition-transform"
        :class="open ? 'rotate-180' : ''"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      v-if="open"
      class="absolute left-3 right-3 top-full mt-1 z-10 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 max-h-64 overflow-y-auto"
    >
      <div v-if="loading" class="px-4 py-3 text-sm text-slate-400">
        {{ t('common.loading') }}
      </div>
      <button
        v-for="r in races"
        :key="r.id"
        type="button"
        class="w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2"
        :class="r.slug === currentRaceSlug
          ? 'text-white font-semibold bg-slate-700'
          : 'text-slate-300 hover:text-white hover:bg-slate-700/50'"
        @click="selectRace(r)"
      >
        <span class="flex-1 truncate">{{ r.name }}</span>
        <svg
          v-if="r.slug === currentRaceSlug"
          class="w-4 h-4 text-emerald-400 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
      </button>
    </div>
  </div>
</template>
